import { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Home, Calendar, MessageCircle, LogOut } from 'lucide-react';
import Messages from '@/components/Messages';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

const CustomerDashboard = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings`);
      setBookings(response.data);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBooking = async (bookingId) => {
    try {
      await axios.put(`${API}/bookings/${bookingId}?status=cancelled`);
      toast.success('Booking cancelled');
      fetchBookings();
    } catch (error) {
      toast.error('Failed to cancel booking');
    }
  };

  const isActive = (path) => location.pathname.includes(path);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="customer-dashboard">
      <div className="flex">
        <aside className="w-64 bg-white border-r min-h-screen p-6">
          <div className="mb-8">
            <Link to="/">
              <div className="text-2xl font-bold gradient-text">BOOK A STAR</div>
            </Link>
            <p className="text-sm text-gray-600 mt-2">Customer Dashboard</p>
          </div>

          <nav className="space-y-2">
            <Link to="/dashboard/customer" data-testid="nav-overview">
              <Button
                variant={isActive('/dashboard/customer') && !isActive('/messages') ? 'secondary' : 'ghost'}
                className="w-full justify-start"
              >
                <Home className="w-4 h-4 mr-2" />
                Overview
              </Button>
            </Link>
            <Link to="/dashboard/customer/bookings" data-testid="nav-bookings">
              <Button
                variant={isActive('/bookings') ? 'secondary' : 'ghost'}
                className="w-full justify-start"
              >
                <Calendar className="w-4 h-4 mr-2" />
                My Bookings
              </Button>
            </Link>
            <Link to="/dashboard/customer/messages" data-testid="nav-messages">
              <Button
                variant={isActive('/messages') ? 'secondary' : 'ghost'}
                className="w-full justify-start"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Messages
              </Button>
            </Link>
          </nav>

          <div className="mt-auto pt-6 border-t">
            <Button
              variant="ghost"
              className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={handleLogout}
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </aside>

        <main className="flex-1 p-8">
          <Routes>
            <Route
              path="/"
              element={
                <div data-testid="overview-content">
                  <h1 className="text-3xl font-semibold mb-6">Welcome, {user?.name}!</h1>
                  
                  <div className="grid md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-white rounded-xl p-6 border border-gray-100">
                      <div className="text-sm text-gray-600 mb-2">Total Bookings</div>
                      <div className="text-3xl font-bold">{bookings.length}</div>
                    </div>
                    <div className="bg-white rounded-xl p-6 border border-gray-100">
                      <div className="text-sm text-gray-600 mb-2">Pending</div>
                      <div className="text-3xl font-bold">
                        {bookings.filter((b) => b.status === 'pending').length}
                      </div>
                    </div>
                    <div className="bg-white rounded-xl p-6 border border-gray-100">
                      <div className="text-sm text-gray-600 mb-2">Confirmed</div>
                      <div className="text-3xl font-bold">
                        {bookings.filter((b) => b.status === 'confirmed').length}
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl border border-gray-100 p-6">
                    <h2 className="text-xl font-semibold mb-4">Recent Bookings</h2>
                    {loading ? (
                      <p className="text-gray-600">Loading...</p>
                    ) : bookings.length === 0 ? (
                      <p className="text-gray-600">No bookings yet</p>
                    ) : (
                      <div className="space-y-4">
                        {bookings.slice(0, 5).map((booking) => (
                          <div
                            key={booking.id}
                            className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                          >
                            <div>
                              <div className="font-semibold">{booking.artist_name}</div>
                              <div className="text-sm text-gray-600">
                                {booking.event_type} • {booking.event_date}
                              </div>
                            </div>
                            <Badge className={getStatusColor(booking.status)}>
                              {booking.status}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              }
            />
            <Route
              path="/bookings"
              element={
                <div data-testid="bookings-content">
                  <h1 className="text-3xl font-semibold mb-6">My Bookings</h1>
                  
                  {loading ? (
                    <p className="text-gray-600">Loading...</p>
                  ) : bookings.length === 0 ? (
                    <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
                      <p className="text-gray-600 mb-4">No bookings yet</p>
                      <Link to="/artists">
                        <Button>Browse Artists</Button>
                      </Link>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {bookings.map((booking) => (
                        <div
                          key={booking.id}
                          className="bg-white rounded-xl border border-gray-100 p-6"
                          data-testid={`booking-${booking.id}`}
                        >
                          <div className="flex items-start justify-between mb-4">
                            <div>
                              <h3 className="text-xl font-semibold">{booking.artist_name}</h3>
                              <p className="text-gray-600">{booking.artist_category}</p>
                            </div>
                            <Badge className={getStatusColor(booking.status)}>
                              {booking.status}
                            </Badge>
                          </div>
                          <div className="grid md:grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-gray-600">Event:</span> {booking.event_type}
                            </div>
                            <div>
                              <span className="text-gray-600">Date:</span> {booking.event_date}
                            </div>
                            <div>
                              <span className="text-gray-600">Location:</span>{' '}
                              {booking.event_location}
                            </div>
                            <div>
                              <span className="text-gray-600">Type:</span> {booking.booking_type}
                            </div>
                          </div>
                          {booking.special_requests && (
                            <div className="mt-4 text-sm">
                              <span className="text-gray-600">Special Requests:</span>{' '}
                              {booking.special_requests}
                            </div>
                          )}
                          {booking.status === 'pending' && (
                            <div className="mt-4">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleCancelBooking(booking.id)}
                                data-testid={`cancel-booking-${booking.id}`}
                              >
                                Cancel Booking
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              }
            />
            <Route path="/messages" element={<Messages />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default CustomerDashboard;
